<template>
  <transition name="modal">
    <div v-if="visible" class="contact-modal-overlay" @click="closeModal">
      <div class="contact-modal" @click.stop>
        <!-- 关闭按钮 -->
        <div class="modal-header">
          <h2 class="modal-title">联系我们</h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        
        <!-- 表单内容 -->
        <div class="modal-content">
          <form @submit.prevent="submitForm" class="contact-form">
            <div class="form-group">
              <label for="name">姓名 *</label>
              <input 
                type="text" 
                id="name" 
                v-model="formData.name" 
                required 
                placeholder="请输入您的姓名"
              >
            </div>
            
            <div class="form-group">
              <label for="phone">手机号 *</label>
              <input 
                type="tel" 
                id="phone" 
                v-model="formData.phone" 
                required 
                placeholder="请输入您的手机号"
                pattern="^1[0-9]{10}$"
                maxlength="11"
                @input="validatePhone"
              >
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="province">省份 *</label>
                <select 
                  id="province" 
                  v-model="formData.province" 
                  @change="onProvinceChange"
                  required
                >
                  <option value="">请选择省份</option>
                  <option v-for="province in areaData.provinces" :key="province.id" :value="province.id">
                    {{ province.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="city">城市 *</label>
                <select 
                  id="city" 
                  v-model="formData.city" 
                  @change="onCityChange"
                  required
                  :disabled="!formData.province"
                >
                  <option value="">请选择城市</option>
                  <option v-for="city in areaData.cities" :key="city.id" :value="city.id">
                    {{ city.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label for="district">区/县 *</label>
              <select 
                id="district" 
                v-model="formData.district" 
                required
                :disabled="!formData.city"
              >
                <option value="">请选择区/县</option>
                <option v-for="district in areaData.districts" :key="district.id" :value="district.id">
                  {{ district.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="appeal">诉求 *</label>
              <textarea 
                id="appeal" 
                v-model="formData.appeal" 
                required 
                placeholder="请详细描述您的需求"
                rows="4"
              ></textarea>
            </div>
            
            <div class="form-group">
              <label>附件</label>
              <div class="file-upload-area">
                <input 
                  type="file" 
                  ref="fileInput" 
                  @change="handleFileChange" 
                  multiple 
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  style="display: none;"
                >
                <div class="upload-trigger" @click="triggerFileUpload">
                  <div class="upload-icon">+</div>
                  <div class="upload-text">点击上传文件</div>
                  <div class="upload-hint">支持 PDF、Word、图片格式，单个文件不超过10MB</div>
                </div>
              </div>
              <div v-if="uploadedFiles.length > 0" class="file-list">
                <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
                  <div class="file-info">
                    <div class="file-name">{{ file.name }}</div>
                    <div class="file-size">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <div class="file-actions">
                    <button type="button" @click="removeFile(index)" class="remove-btn">删除</button>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" class="cancel-btn" @click="closeModal">取消</button>
              <button type="submit" class="submit-btn" :disabled="submitting">
                {{ submitting ? '提交中...' : '提交' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { submitCase, uploadFile, getAreaList } from '@/api';

export default {
  name: 'ContactModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        name: '',
        phone: '',
        appeal: '',
        province: '',
        city: '',
        district: '',
        // submit_time: ''
      },
      submitting: false,
      uploadedFiles: [],
      areaData: {
        provinces: [],
        cities: [],
        districts: []
      }
    }
  },
  created() {
    this.fetchProvinces();
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    async fetchProvinces() {
      try {
        const res = await getAreaList();
        if (res && res.data) {
          this.areaData.provinces = res.data;
        }
      } catch (error) {
        console.error('获取省份列表失败:', error);
      }
    },
    async fetchCities(provinceId) {
      try {
        const res = await getAreaList({ province: provinceId });
        if (res && res.data) {
          this.areaData.cities = res.data;
        }
      } catch (error) {
        console.error('获取城市列表失败:', error);
      }
    },
    async fetchDistricts(cityId) {
      try {
        const res = await getAreaList({ 
          province: this.formData.province, 
          city: cityId 
        });
        if (res && res.data) {
          this.areaData.districts = res.data;
        }
      } catch (error) {
        console.error('获取区县列表失败:', error);
      }
    },
    onProvinceChange() {
      this.formData.city = '';
      this.formData.district = '';
      this.areaData.cities = [];
      this.areaData.districts = [];
      if (this.formData.province) {
        this.fetchCities(this.formData.province);
      }
    },
    onCityChange() {
      this.formData.district = '';
      this.areaData.districts = [];
      if (this.formData.city) {
        this.fetchDistricts(this.formData.city);
      }
    },
    async uploadSingleFile(file) {
      try {
        const res = await uploadFile(file);
        if (res && res.code === 1 && res.data) {
          return res.data.fullurl;
        }
        throw new Error('文件上传失败');
      } catch (error) {
        console.error('文件上传失败:', error);
        throw error;
      }
    },
    formatDateTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    async submitForm() {
      if (this.submitting) return;
      
      // 验证手机号格式
      const phoneRegex = /^1[0-9]{10}$/;
      if (!phoneRegex.test(this.formData.phone)) {
        alert('请输入正确的手机号格式');
        return;
      }
      
      this.submitting = true;
      try {
        // 先上传所有文件
        const uploadPromises = this.uploadedFiles.map(fileObj => 
          this.uploadSingleFile(fileObj.file)
        );
        
        const uploadedUrls = await Promise.all(uploadPromises);
        
        // 设置提交时间，格式化为可视化时间
        // this.formData.submit_time = this.formatDateTime(new Date());
        
        // 准备提交数据
        const submitData = {
          ...this.formData,
          upload_files: uploadedUrls.join(',')
        };
        
        const res = await submitCase(submitData);
        if (res && res.code === 1) {
          alert('提交成功！我们会尽快与您联系。');
          this.resetForm();
          this.closeModal();
        } else {
          alert('提交失败，请稍后重试。');
        }
      } catch (error) {
        console.error('提交联系表单失败:', error);
        alert('提交失败，请稍后重试。');
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        phone: '',
        appeal: '',
        province: '',
        city: '',
        district: '',
        // submit_time: ''
      };
      this.uploadedFiles = [];
      this.areaData.cities = [];
      this.areaData.districts = [];
      // 清空文件输入框
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    validatePhone() {
      // 只保留数字
      this.formData.phone = this.formData.phone.replace(/\D/g, '');
      
      // 如果第一位不是1，清空输入
      if (this.formData.phone.length > 0 && this.formData.phone[0] !== '1') {
        this.formData.phone = '';
      }
      
      // 限制最多11位
      if (this.formData.phone.length > 11) {
        this.formData.phone = this.formData.phone.slice(0, 11);
      }
    },
    handleFileChange(event) {
      const files = event.target.files;
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif'];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // 检查文件大小
        if (file.size > maxSize) {
          alert(`文件 ${file.name} 超过10MB限制，请选择更小的文件。`);
          continue;
        }
        
        // 检查文件类型
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(fileExtension)) {
          alert(`文件 ${file.name} 格式不支持，请选择 PDF、Word 或图片格式的文件。`);
          continue;
        }
        
        // 检查是否重复
        const isDuplicate = this.uploadedFiles.some(existingFile => 
          existingFile.name === file.name && existingFile.size === file.size
        );
        
        if (isDuplicate) {
          alert(`文件 ${file.name} 已存在，请选择其他文件。`);
          continue;
        }
        
        this.uploadedFiles.push({
          name: file.name,
          size: file.size,
          file: file // 保存原始文件对象，用于后续上传
        });
      }
      
      // 清空input值，允许重复选择同一文件
      event.target.value = '';
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    }
  }
}
</script>

<style scoped>
.contact-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.contact-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 20px;
}

.modal-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.close-btn:hover {
  background-color: #f5f5f5;
  color: #666;
}

.modal-content {
  padding: 24px;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row .form-group {
  flex: 1;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 12px 16px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s, box-shadow 0.3s;
  background: #fff;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3159B3;
  box-shadow: 0 0 0 3px rgba(49, 89, 179, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-group select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.cancel-btn,
.submit-btn {
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.cancel-btn {
  background: #f5f5f5;
  color: #666;
}

.cancel-btn:hover {
  background: #e8e8e8;
  color: #333;
}

.submit-btn {
  background: #3159B3;
  color: #fff;
}

.submit-btn:hover:not(:disabled) {
  background: #2A4A9B;
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 文件上传样式 */
.file-upload-area {
  margin-top: 8px;
}

.upload-trigger {
  border: 2px dashed #E0E0E0;
  border-radius: 8px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;
}

.upload-trigger:hover {
  border-color: #3159B3;
  background-color: #f8f9fa;
}

.upload-icon {
  font-size: 28px;
  color: #999;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
}

.upload-hint {
  font-size: 12px;
  color: #999;
}

.file-list {
  margin-top: 12px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 6px;
}

.file-info {
  flex: 1;
}

.file-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.file-actions {
  margin-left: 12px;
}

.remove-btn {
  background: #ff4757;
  color: #fff;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.remove-btn:hover {
  background: #ff3742;
}

/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .contact-modal,
.modal-leave-active .contact-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .contact-modal,
.modal-leave-to .contact-modal {
  transform: scale(0.9);
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .contact-modal {
    max-width: 100%;
    margin: 20px;
  }
  
  .modal-header {
    padding: 20px 20px 0 20px;
  }
  
  .modal-content {
    padding: 20px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-row {
    flex-direction: column;
    gap: 20px;
  }
}
</style>
